import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../../context/MyContext";
import {
  backIncstance,
  formatDateString,
} from "../../../utils/commonFunctions";

// YYYY.MM.DD 형식으로 날짜 포맷팅
export const formatDateDot = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
};

const URLS = {
  GET_ORDER_LIST: process.env.REACT_APP_MYPAGE_GET_ORDER_LIST,
  HELP: process.env.REACT_APP_DONGA_INQUERY,
};

export interface OrderItem {
  order_no: string;
  item_name: string;
  order_price: number;
  created_at: string;
  is_canceled: boolean;
}

interface PageInfo {
  offset: number;
  limit: number;
  total: number;
}

/**
 * 이용 내역 페이지 Presentation Hook
 *
 * - 주문 리스트 조회
 * - 페이지네이션
 * - 네비게이션 핸들러
 */
export const useOrderListView = () => {
  const navigate = useNavigate();
  const { user, setAlertSetting, isLogin, setIsLogin } = useContext(MyContext);

  const [orderList, setOrderList] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    offset: 1,
    limit: 10,
    total: 0,
  });

  // 이용 기간 계산
  const subscriptionPeriod = user?.paid
    ? {
        startDate: formatDateDot(user.start_dt),
        endDate: formatDateDot(user.end_dt),
      }
    : null;

  // API 호출 유틸
  const touchAPI = async (method: string, url: string, data?: unknown) => {
    try {
      const response = await backIncstance(method, url, data);
      if (response.status === 401) {
        setAlertSetting((prev: Record<string, unknown>) => ({
          ...prev,
          alertTxt: "잠시 후 다시 시도해주세요.",
        }));
        return null;
      }
      return response;
    } catch {
      setAlertSetting((prev: Record<string, unknown>) => ({
        ...prev,
        alertTxt: "잠시 후 다시 시도해주세요.",
      }));
      return null;
    }
  };

  // 주문 목록 조회
  const getData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const url = `${URLS.GET_ORDER_LIST}?offset=${pageInfo.offset}&limit=${pageInfo.limit}`;
      const resData = await touchAPI("GET", url);
      if (resData) {
        const data = await resData.json();
        setOrderList(data.order_list || []);
        setPageInfo((prev) => ({
          ...prev,
          total: data.total_count || 0,
        }));
      }
    } catch {
      setAlertSetting((prev: Record<string, unknown>) => ({
        ...prev,
        alertTxt: "서버 오류입니다.\n관리자에게 문의 바랍니다.",
        btnTxt: "닫기",
      }));
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드 및 페이지 변경 시 데이터 조회
  useEffect(() => {
    if (!user?.id) {
      setIsLogin(true);
      return;
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pageInfo.offset]);

  // 뒤로가기 핸들러
  const handleGoBack = () => {
    navigate(-1);
  };

  // 상세 페이지 이동 핸들러
  const handleDetailClick = (item: OrderItem) => {
    const myPageUrl = process.env.REACT_APP_DONGA_MYPAGE;
    // 이용권/무료체험권인 경우 voucher 페이지로, 그 외에는 orders/delivery 페이지로
    const isVoucher = item.item_name?.includes("무료") || item.item_name?.includes("이용권");
    const url = isVoucher
      ? `${myPageUrl}/voucher`
      : `${myPageUrl}/orders/delivery?status=Completed&page=1`;
    window.open(url, "_blank");
  };

  return {
    // 상태
    user,
    orderList,
    loading,
    pageInfo,
    subscriptionPeriod,
    isLogin,

    // 핸들러
    handleGoBack,
    handleDetailClick,
    setPageInfo,
  };
};

export default useOrderListView;
